import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import EditIcon from '@mui/icons-material/Edit'
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField,
  Tooltip
} from '@mui/material'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import { useAppTranslation } from 'src/admin/core/hooks'
import { 
  Assignment, 
  ScreenDetail,
  upsertScreen,
  syncAssignments
} from 'src/admin/core/services/screens'
import { useSnackbar } from 'notistack'
import { useScreensContext } from '../ScreensContext'
import { getAppBarState } from 'src/store'

const ConfigurarAssignments = ({ isActive }: { isActive?: boolean }) => {
  const { label } = useAppTranslation()
  const { enqueueSnackbar } = useSnackbar()
  
  const [selectedScreenId, setSelectedScreenId] = useState<string | null>(null)
  
  const [localScreenName, setLocalScreenName] = useState('')
  const [localAssignments, setLocalAssignments] = useState<string[]>([])
  const [originalScreen, setOriginalScreen] = useState<ScreenDetail | null>(null)

  const [openDialog, setOpenDialog] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'rename'>('create')
  const [screenNameInput, setScreenNameInput] = useState('')

  const [syncLoading, setSyncLoading] = useState(false)
  const [syncAlreadyDone, setSyncAlreadyDone] = useState(false)

  const {
    setTabDirty,
    registerSaveHandler,
    screens,
    assignments: availableAssignments,
    enterprise,
    loadingSharedData: loading,
    sharedDataError: error,
    reloadSharedData,
    setAssignments,
  } = useScreensContext()
  const { userEnterprise } = getAppBarState().appState

  const enterpriseBaseUrl = enterprise?.base_url || ''

  const handleSyncAssignments = async () => {
    setSyncLoading(true)
    try {
      const res = await syncAssignments(userEnterprise.id)
      if (res.success) {
        // Mezclar la data de esos assignments con los obtenidos en el endpoint screen-administration-data
        // Ordenar con la misma logica que existe actualmente.
        setAssignments(prev => {
          const newAssignments = res.data || []
          const combined = [...prev, ...newAssignments]
          // Filter duplicates by _id
          const uniqueMap = new Map<string, Assignment>()
          combined.forEach(a => {
            if (a && a._id) {
              uniqueMap.set(a._id, a)
            }
          })
          
          return Array.from(uniqueMap.values()).sort((a, b) => (a.order || 0) - (b.order || 0))
        })
        
        enqueueSnackbar(res.message || 'Assignments actualizados correctamente', { variant: 'success' })
        setSyncAlreadyDone(true)
      }
    } catch (err: any) {
        enqueueSnackbar(err.message || 'Error al actualizar assignments', { variant: 'error' })
    } finally {
        setSyncLoading(false)
    }
  }

  // Auto-select first screen when screens load
  useEffect(() => {
    if (screens.length > 0 && !selectedScreenId) {
      setSelectedScreenId(screens[0]._id)
    }
  }, [screens, selectedScreenId])

  // Reset sync button when user enters the tab
  useEffect(() => {
    if (isActive) {
      setSyncAlreadyDone(false)
    }
  }, [isActive])

  // Sync local state when selected screen changes
  useEffect(() => {
    if (selectedScreenId) {
      const screen = screens.find(s => s._id === selectedScreenId)
      if (screen) {
        setLocalScreenName(screen.name)
        setLocalAssignments(screen.assignments || [])
        setOriginalScreen(JSON.parse(JSON.stringify(screen)))
        setTabDirty('assignments', false)
      }
    } else if (selectedScreenId === null && screens.length === 0) {
      // No screens yet, maybe initial load
    }
  }, [selectedScreenId, screens, setTabDirty])

  // Monitor changes to enable Save button
  useEffect(() => {
    if (!originalScreen && selectedScreenId === 'new') {
      setTabDirty('assignments', true)
      return
    }

    if (originalScreen) {
      const nameChanged = localScreenName !== originalScreen.name
      const assignmentsChanged = JSON.stringify(localAssignments) !== JSON.stringify(originalScreen.assignments)
      setTabDirty('assignments', nameChanged || assignmentsChanged)
    }
  }, [localScreenName, localAssignments, originalScreen, selectedScreenId, setTabDirty])

  const selectedScreen = screens.find((s) => s._id === selectedScreenId)

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const items = Array.from(localAssignments)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    setLocalAssignments(items)
  }

  const handleAddAssignment = (text: string) => {
    if (localAssignments.includes(text)) return
    setLocalAssignments([...localAssignments, text])
  }

  const handleRemoveAssignment = (text: string) => {
    setLocalAssignments(localAssignments.filter(a => a !== text))
  }

  const handleOpenCreateDialog = () => {
    setDialogMode('create')
    setScreenNameInput(`Screen ${screens.length + 1}`)
    setOpenDialog(true)
  }

  const handleOpenRenameDialog = (e: React.MouseEvent, screen: ScreenDetail) => {
    e.stopPropagation()
    setSelectedScreenId(screen._id)
    setDialogMode('rename')
    setScreenNameInput(screen.name)
    setOpenDialog(true)
  }

  const handleDialogClose = () => {
    setOpenDialog(false)
    setScreenNameInput('')
  }

  const handleDialogConfirm = () => {
    if (!screenNameInput.trim()) return

    if (dialogMode === 'create') {
      setSelectedScreenId('new')
      setLocalScreenName(screenNameInput)
      setLocalAssignments([])
      setOriginalScreen(null)
    } else {
      setLocalScreenName(screenNameInput)
    }
    setOpenDialog(false)
  }

  const handleSave = async () => {
    try {
      const payload = {
        ...(selectedScreenId !== 'new' ? { _id: selectedScreenId! } : {}),
        name: localScreenName,
        enterprise_id: userEnterprise.id,
        assignments: localAssignments
      }
      
      const res = await upsertScreen(payload)
      if (res.success) {
        enqueueSnackbar(res.message || label('CAMBIOS_GUARDADOS'), { variant: 'success' })
        setTabDirty('assignments', false)
        await reloadSharedData()
        if (res.inserted_id) {
          setSelectedScreenId(res.inserted_id.toString())
        }
      } else {
        enqueueSnackbar(label('ERROR_AL_GUARDAR'), { variant: 'error' })
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || label('ERROR_AL_GUARDAR'), { variant: 'error' })
    }
  }

  useEffect(() => {
    registerSaveHandler('assignments', handleSave)
  }, [registerSaveHandler, localScreenName, localAssignments, selectedScreenId])

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
      enqueueSnackbar(label('COPIADO'), { variant: 'success' })
    } else {
      // Fallback for non-secure contexts (HTTP)
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand('copy')
        enqueueSnackbar(label('COPIADO'), { variant: 'success' })
      } catch (err) {
        enqueueSnackbar(label('ERROR_AL_COPIAR'), { variant: 'error' })
      }
      document.body.removeChild(textArea)
    }
  }

  // Group available assignments by Category
  const groupedAssignments = availableAssignments.reduce(
    (acc, assignment) => {
      const category = assignment.category || 'Sin Categoría'
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(assignment)
      return acc
    },
    {} as Record<string, Assignment[]>,
  )

  const sortedCategories = Object.keys(groupedAssignments).sort((a, b) => a.localeCompare(b))

  const generatePreviewUrl = () => {
    try {
      const base = enterpriseBaseUrl || (selectedScreen?.url ? selectedScreen.url.split('?')[0] : '')
      if (!base) return ''
      
      const absoluteBase = base.includes('://') ? base : `http://${base}`
      const urlObj = new URL(absoluteBase)
      
      // Remove any existing SPORTS parameter (case-insensitive check) to avoid duplicates
      const paramsToRemove: string[] = []
      urlObj.searchParams.forEach((_, key) => {
        if (key.toUpperCase() === 'SPORTS') {
          paramsToRemove.push(key)
        }
      })
      paramsToRemove.forEach(p => urlObj.searchParams.delete(p))
      
      if (localAssignments.length > 0) {
        const sportsParam = localAssignments.join(',').toUpperCase()
        urlObj.searchParams.set('SPORTS', sportsParam)
      }
      
      // decodeURIComponent ensures the user sees commas (,) instead of %2C
      return decodeURIComponent(urlObj.toString())
    } catch (e) {
      return enterpriseBaseUrl || (selectedScreen?.url || '')
    }
  }

  const previewUrl = generatePreviewUrl()

  return (
    <Box sx={{ display: 'flex', gap: 3, minHeight: 500 }}>
      {/* Left column - Screens list */}
      <Box sx={{ minWidth: 220, maxWidth: 260 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1.5, mb: 1, display: 'block' }}
        >
          {label('MIS_SCREENS')}
        </Typography>
        <List disablePadding>
          {screens.map((screen) => (
            <ListItemButton
              key={screen._id}
              selected={selectedScreenId === screen._id}
              onClick={() => setSelectedScreenId(screen._id)}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                border: '1px solid',
                borderColor: selectedScreenId === screen._id ? 'primary.main' : 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'background.paper',
                  borderColor: 'primary.main',
                },
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {screen.name}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleOpenRenameDialog(e, screen)}
                      sx={{ p: 0.5 }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {screen.assignments?.length || 0} Assignments
                  </Typography>
                }
              />
            </ListItemButton>
          ))}
          {/* Temporary New Screen in list */}
          {selectedScreenId === 'new' && (
            <ListItemButton
              selected
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                border: '1px solid',
                borderColor: 'primary.main',
                backgroundColor: 'background.paper',
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {localScreenName} (Nuevo)
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setDialogMode('rename')
                        setScreenNameInput(localScreenName)
                        setOpenDialog(true)
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {localAssignments.length} Assignments
                  </Typography>
                }
              />
            </ListItemButton>
          )}
        </List>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          fullWidth
          sx={{ mt: 1, borderStyle: 'dashed', textTransform: 'none' }}
          onClick={handleOpenCreateDialog}
        >
          {label('NUEVO_SCREEN')}
        </Button>
      </Box>

      {/* Center column - Available assignments */}
      <Box sx={{ flex: 1, minWidth: 300 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AddIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {label('DISPONIBLES')}
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            size="small" 
            startIcon={<RefreshIcon />} 
            color="success" 
            sx={{ textTransform: 'none' }}
            onClick={handleSyncAssignments}
            disabled={syncLoading || syncAlreadyDone}
          >
            {syncLoading ? label('CARGANDO') : label('ACTUALIZAR_ASSIGNMENTS')}
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={30} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && availableAssignments.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No se encontraron assignments.
          </Typography>
        )}

        <Box sx={{ maxHeight: '72vh', overflowY: 'auto', pr: 1 }}>
          {sortedCategories.map((category) => (
            <Box key={category} sx={{ mb: 2 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1.5, fontSize: '0.65rem' }}
              >
                {category.toUpperCase()}
              </Typography>
              {groupedAssignments[category].map((assignment) => (
                <Card
                  key={assignment.text}
                  variant="outlined"
                  sx={{ mb: 1, '&:hover': { borderColor: 'primary.main' } }}
                >
                  <CardContent
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:last-child': { pb: 1.5 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="body2">{assignment.text}</Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => handleAddAssignment(assignment.text)}
                      disabled={localAssignments.includes(assignment.text)}
                    >
                      <AddIcon fontSize="small" color={localAssignments.includes(assignment.text) ? 'disabled' : 'primary'} />
                    </IconButton>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right column - Assigned + Preview */}
      <Box sx={{ flex: 1, minWidth: 280 }}>
        {/* Assigned assignments */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {label('EN_ESTE_SCREEN')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {label('DRAG_TO_REORDER')}
            </Typography>
          </Box>

          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="assignments">
              {(provided) => (
                <Stack 
                  {...provided.droppableProps} 
                  ref={provided.innerRef} 
                  spacing={1}
                >
                  {localAssignments.map((assignmentName, index) => (
                    <Draggable key={assignmentName} draggableId={assignmentName} index={index}>
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            p: 1.5,
                            borderRadius: 1.5,
                            backgroundColor: 'text.primary',
                            color: 'background.paper',
                            fontWeight: 500,
                            fontSize: '0.8125rem',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DragIndicatorIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {assignmentName}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={() => handleRemoveAssignment(assignmentName)}
                            sx={{ color: 'background.paper', opacity: 0.8, '&:hover': { opacity: 1 } }}
                          >
                            <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        </Box>

        {/* Preview URL */}
        {/* <Box>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5, 
              mb: 1, 
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => previewUrl && window.open(previewUrl, '_blank')}
          >
            <OpenInNewIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {label('VISTA_PREVIA_URL')}
            </Typography>
          </Box>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                {label('CLICK_PARA_ABRIR')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Link
                  href={previewUrl}
                  target="_blank"
                  rel="noopener"
                  variant="caption"
                  sx={{ 
                    wordBreak: 'break-all',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {previewUrl}
                </Link>
                <IconButton size="small" onClick={() => copyToClipboard(previewUrl)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        </Box> */}
      </Box>

      {/* New/Rename Screen Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? label('NUEVO_SCREEN') : label('RENOMBRAR_SCREEN')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={label('NOMBRE_DEL_SCREEN')}
            fullWidth
            variant="outlined"
            value={screenNameInput}
            onChange={(e) => setScreenNameInput(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleDialogClose} color="inherit">
            {label('CANCELAR')}
          </Button>
          <Button onClick={handleDialogConfirm} variant="contained">
            {label('ACEPTAR')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ConfigurarAssignments
